import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Briefcase, Users, ShieldCheck, TrendingUp, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import TimeToHireCard from "@/components/TimeToHireCard";

const Stat = ({ icon: Icon, label, value, delta, tone = "pink" }) => (
  <Card className="border-0 shadow-sm card-lift">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium">{label}</div>
          <div className="text-3xl font-bold mt-2">{value}</div>
          {delta && <div className="text-xs text-teal mt-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />{delta}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg grid place-items-center`} style={{ backgroundColor: tone === "pink" ? "#FFE0F0" : tone === "teal" ? "#DFFBEC" : "#E6EAF2" }}>
          <Icon className="w-5 h-5" style={{ color: tone === "pink" ? "#FF007F" : tone === "teal" ? "#0CDC73" : "#00214D" }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.get("/analytics/hiring-funnel").then(r => setFunnel(r.data)).catch(() => {});
    api.get("/compliance/health").then(r => setHealth(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Dashboard</div>
          <h1 className="text-3xl font-bold mt-1">Good day, {user?.name?.split(" ")[0] || "there"}.</h1>
          <p className="text-neutral-600 mt-1">Here's what's happening across your workspace.</p>
        </div>
        <Link to="/app/recruit/jobs" data-testid="dashboard-new-job-btn">
          <Button className="btn-primary rounded-full">Open Recruit</Button>
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Briefcase} label="Open Jobs" value={funnel?.total_jobs ?? "—"} delta="+2 this week" tone="pink" />
        <Stat icon={Users} label="Candidates" value={funnel?.total_candidates ?? "—"} delta="+11 last 7 days" tone="navy" />
        <Stat icon={TrendingUp} label="Employees" value={funnel?.total_employees ?? "—"} tone="teal" />
        <Stat icon={ShieldCheck} label="Compliance Score" value={health ? `${health.score}%` : "—"} delta={health ? `${health.counts.passing}/${health.counts.total} passing` : ""} tone="teal" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TimeToHireCard />
        </div>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Compliance health</h3>
            {health ? (
              <div>
                <div className="flex items-end gap-3">
                  <div className="text-5xl font-bold text-pink">{health.score}</div>
                  <div className="text-neutral-500 pb-2">/ 100</div>
                </div>
                <div className="text-xs text-neutral-500 mt-2">{health.framework}</div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-xl font-bold text-teal">{health.counts.passing}</div><div className="text-[10px] uppercase text-neutral-500">Passing</div></div>
                  <div><div className="text-xl font-bold" style={{color:"#F6AD55"}}>{health.counts.warning}</div><div className="text-[10px] uppercase text-neutral-500">Warning</div></div>
                  <div><div className="text-xl font-bold text-red-500">{health.counts.failing}</div><div className="text-[10px] uppercase text-neutral-500">Failing</div></div>
                </div>
              </div>
            ) : <div className="text-neutral-500">Loading…</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
